import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { format, parseISO, addDays, differenceInCalendarDays } from "date-fns";
import { mdy, md } from "@/lib/dateFmt";
import { CalendarClock, Check, AlertTriangle, Clock, Sun, Sunset, Moon, Users2, Pencil, RotateCcw, Save, Sparkles, ChevronDown, ChevronRight, X, Archive, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import AdminAvailabilityEditor from "@/components/AdminAvailabilityEditor";

function MinInput({ label, icon: Icon, value, onChange, testid }) {
  return (
    <label className="flex items-center gap-2 rounded-lg border border-slate-200 px-2 py-1.5 bg-white">
      <Icon size={13} className="text-slate-500" />
      <span className="text-[11px] text-slate-600 flex-1 truncate">{label}</span>
      <input
        type="number" min="0" step="1"
        value={value ?? 0}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-14 rounded-md border border-slate-300 px-1.5 py-1 text-sm tabular-nums text-right focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
    </label>
  );
}

const RULES_DEFAULTS = {
  availability_due_offset_days: 23,
  availability_monthly_offset_days: 7,
  availability_window_pps: 2,
  min_total_days: 0,
  min_weekend_days: 0,
  min_day_shifts: 0,
  min_evening_shifts: 0,
  min_night_shifts: 0,
};

function AvailabilityRulesInline({ facilityId, facilityLabel }) {
  const [cadence, setCadence] = useState("biweekly");
  const [settings, setSettings] = useState({ ...RULES_DEFAULTS });
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);

  const load = async () => {
    if (!facilityId) return;
    try {
      const [meta, s] = await Promise.all([
        api.get(`/facility-meta?facility_id=${facilityId}`),
        api.get(`/facilities/${facilityId}/settings`),
      ]);
      setCadence(meta.data?.cadence || "biweekly");
      setSettings({ ...RULES_DEFAULTS, ...s.data });
    } catch { /* silent */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [facilityId]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/facilities/${facilityId}/settings`, {
        availability_due_offset_days:      Number(settings.availability_due_offset_days) || 23,
        availability_monthly_offset_days:  Number(settings.availability_monthly_offset_days) || 7,
        availability_window_pps:           [1, 2].includes(Number(settings.availability_window_pps)) ? Number(settings.availability_window_pps) : 2,
        min_total_days:                    Number(settings.min_total_days)      || 0,
        min_weekend_days:                  Number(settings.min_weekend_days)    || 0,
        min_day_shifts:                    Number(settings.min_day_shifts)      || 0,
        min_evening_shifts:                Number(settings.min_evening_shifts)  || 0,
        min_night_shifts:                  Number(settings.min_night_shifts)    || 0,
      });
      setSettings({ ...RULES_DEFAULTS, ...data });
      toast.success("Availability rules saved");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed"); }
    setSaving(false);
  };
  const setField = (k, v) => setSettings((s) => ({ ...s, [k]: v }));

  const minsSummary = ["Total", "Wk", "D", "E", "N"]
    .map((k, i) => {
      const v = [settings.min_total_days, settings.min_weekend_days, settings.min_day_shifts, settings.min_evening_shifts, settings.min_night_shifts][i];
      return v > 0 ? `${k}·${v}` : null;
    })
    .filter(Boolean)
    .join(" ");

  return (
    <div className="mx-5 mt-4 rounded-xl border border-slate-200 bg-slate-50/60" data-testid="availability-rules-inline">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left"
        data-testid="availability-rules-toggle"
      >
        <div className="flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-black text-slate-700 rounded-md border border-slate-300 bg-white px-2 py-0.5">
            <Sparkles size={11} className="text-amber-600" /> Rules
          </span>
          <span className="text-[12px] text-slate-600">
            {minsSummary
              ? <>Minimum every submission · <span className="font-medium">{minsSummary}</span></>
              : "No minimum requirements set"}
          </span>
        </div>
        <ChevronDown size={14} className={`text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="border-t border-slate-200 p-3">
          {cadence === "biweekly" && (
            <div className="border border-slate-200 rounded-lg p-3 bg-white mb-3" data-testid="rules-window-pps">
              <Label className="text-xs font-medium text-slate-800 flex items-center gap-1.5">
                <CalendarClock size={12} className="text-blue-600" /> Availability window length
              </Label>
              <div className="mt-2 inline-flex rounded-lg border border-slate-300 bg-slate-50 p-0.5 text-[11px] font-semibold">
                {[
                  { v: 1, label: "1 pay period (14 days)" },
                  { v: 2, label: "2 pay periods (28 days)" },
                ].map((opt) => {
                  const active = Number(settings.availability_window_pps || 2) === opt.v;
                  return (
                    <button
                      key={opt.v}
                      type="button"
                      onClick={() => setField("availability_window_pps", opt.v)}
                      data-testid={`window-pps-${opt.v}`}
                      className={`px-3 py-1 rounded-md transition-colors ${active ? "bg-blue-600 text-white" : "text-slate-600 hover:text-slate-900"}`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              <div className="text-[10px] text-slate-500 mt-1.5">
                Schedules still post one pay period at a time. This only changes how many pay periods each availability submission covers.
              </div>
            </div>
          )}
          <div className="border border-slate-200 rounded-lg p-3 bg-white" data-testid="rules-mins">
            <Label className="text-xs font-medium text-slate-800 flex items-center gap-1.5">
              <Sparkles size={12} className="text-amber-600" /> Minimum every submission
            </Label>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-1.5 mt-2">
              <MinInput label="Total days"    icon={CalendarClock} value={settings.min_total_days}     onChange={(v) => setField("min_total_days",     v)} testid="min-total" />
              <MinInput label="Weekend days"  icon={CalendarClock} value={settings.min_weekend_days}   onChange={(v) => setField("min_weekend_days",   v)} testid="min-weekend" />
              <MinInput label="Day slots (D)" icon={Sun}           value={settings.min_day_shifts}     onChange={(v) => setField("min_day_shifts",     v)} testid="min-day" />
              <MinInput label="Evening (E)"   icon={Sunset}        value={settings.min_evening_shifts} onChange={(v) => setField("min_evening_shifts", v)} testid="min-evening" />
              <MinInput label="Night (N)"     icon={Moon}          value={settings.min_night_shifts}   onChange={(v) => setField("min_night_shifts",   v)} testid="min-night" />
            </div>
            <div className="text-[10px] text-slate-500 mt-1.5">Below-minimum submissions are hard-blocked. <span className="font-medium">Total days</span> counts each calendar day where at least one code (D/E/N) is checked — combining codes on the same day still counts as 1 day.</div>
          </div>
          <div className="flex justify-end mt-3">
            <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={save} disabled={saving} data-testid="rules-save">
              <Save size={13} className="mr-1" /> {saving ? "Saving…" : "Save rules"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function ArchivedToggle({ showArchived, onToggle, count }) {
  return (
    <button
      type="button"
      onClick={() => onToggle(!showArchived)}
      data-testid="availability-archived-toggle"
      className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-widest transition-colors ${
        showArchived
          ? "border-slate-700 bg-slate-800 text-white hover:bg-slate-900"
          : "border-slate-300 bg-white text-slate-600 hover:bg-slate-50 hover:border-slate-400"
      }`}
    >
      <Archive size={12} />
      {showArchived ? "Hide archived" : "Show archived"}
      {!showArchived && count > 0 && (
        <span className="rounded-full bg-slate-100 text-slate-700 text-[9px] font-bold px-1.5 tabular-nums">{count}</span>
      )}
    </button>
  );
}

function InlineDueEditor({ periodId, value, hasOverride, onSave, onRevert }) {
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState(value || "");
  useEffect(() => { setVal(value || ""); }, [value]);
  if (!open) {
    return (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        data-testid={`avail-due-edit-${periodId}`}
        title="Edit availability due date"
        className="ml-1 inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-1.5 py-0.5 text-[10px] text-slate-600 hover:bg-blue-50 hover:border-blue-400 hover:text-blue-700"
      >
        <Pencil size={10} /> Edit due
      </button>
    );
  }
  return (
    <div className="inline-flex items-center gap-1 ml-1" onClick={(e) => e.stopPropagation()}>
      <input
        type="date"
        value={val}
        onChange={(e) => setVal(e.target.value)}
        data-testid={`avail-due-input-${periodId}`}
        className="rounded-md border border-slate-300 px-1.5 py-0.5 text-[11px] tabular-nums focus:outline-none focus:ring-2 focus:ring-blue-300"
      />
      <button
        type="button"
        onClick={() => { onSave(val); setOpen(false); }}
        data-testid={`avail-due-save-${periodId}`}
        className="rounded-md bg-blue-600 hover:bg-blue-700 text-white text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5"
      >
        Save
      </button>
      {hasOverride && (
        <button
          type="button"
          onClick={() => { onRevert(); setOpen(false); }}
          data-testid={`avail-due-revert-${periodId}`}
          title="Revert to facility default"
          className="rounded-md border border-slate-300 bg-white text-slate-600 hover:bg-slate-50 px-1.5 py-0.5 text-[10px]"
        >
          <RotateCcw size={10} />
        </button>
      )}
      <button
        type="button"
        onClick={() => { setOpen(false); setVal(value || ""); }}
        className="text-slate-400 hover:text-slate-600 text-[10px] px-1"
      >
        ✕
      </button>
    </div>
  );
}

const SLOT_META = {
  D: { label: "Days",     icon: Sun,     cls: "bg-amber-100 text-amber-800 border-amber-300" },
  E: { label: "Evenings", icon: Sunset,  cls: "bg-orange-100 text-orange-800 border-orange-300" },
  N: { label: "Nights",   icon: Moon,    cls: "bg-indigo-100 text-indigo-800 border-indigo-300" },
};

const statusPillFor = (row) => {
  if (row.submitted) {
    return row.is_late
      ? { label: "Late", cls: "bg-amber-100 text-amber-800 border-amber-300" }
      : { label: "Submitted", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" };
  }
  return row.is_overdue
    ? { label: "Overdue", cls: "bg-red-100 text-red-800 border-red-300" }
    : { label: "Pending", cls: "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300" };
};

export default function AvailabilityPage({ facilityId: propFacilityId }) {
  const { user } = useAuth();
  const isEmployee = user?.role === "employee";
  // For employees, always anchor to their PRIMARY facility (with legacy
  // fallback to `facility_ids[0]`). This keeps the availability tab pointed
  // at their home facility even when they switch the schedule view to a
  // secondary one — since secondary facilities are view + pickup only.
  const facilityId = isEmployee
    ? (user?.primary_facility_id || (user?.facility_ids || [])[0] || propFacilityId)
    : propFacilityId;
  const [items, setItems] = useState([]);
  const [openPP, setOpenPP] = useState(null);
  const [days, setDays] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [myEditReqs, setMyEditReqs] = useState([]);
  const [reason, setReason] = useState("");
  const [settings, setSettings] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  // Admin-only: how many future windows to load. Default 5 (current + 4 out);
  // admins can crank up to 24 so per diems can submit months in advance.
  const isAdmin = user?.role === "admin";
  const isManager = user?.role === "admin" || user?.role === "manager";
  const [horizon, setHorizon] = useState(() => {
    const saved = parseInt(localStorage.getItem("availability_horizon") || "5", 10);
    return Math.max(1, Math.min(24, isNaN(saved) ? 5 : saved));
  });
  useEffect(() => { localStorage.setItem("availability_horizon", String(horizon)); }, [horizon]);
  // Per-user hidden window ids (admin only) so you can "delete" past clutter.
  const [hiddenIds, setHiddenIds] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("availability_hidden_ids") || "[]")); }
    catch { return new Set(); }
  });
  const persistHidden = (s) => {
    setHiddenIds(new Set(s));
    localStorage.setItem("availability_hidden_ids", JSON.stringify([...s]));
  };
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [adminEdit, setAdminEdit] = useState(null);  // { pp, emp } — opens editor overlay
  // Assignments in the currently-open window, keyed by ISO date. Used to
  // flag the "you're already scheduled — file PTO instead" hard-block and
  // to compute the < 14-day (or < 24-hour for Solano) notice caution.
  const [openPPAssignments, setOpenPPAssignments] = useState({});
  const toggleExpanded = (id) => {
    setExpandedIds((prev) => {
      const nxt = new Set(prev);
      if (nxt.has(id)) nxt.delete(id); else nxt.add(id);
      return nxt;
    });
  };

  const load = async () => {
    try {
      const q = new URLSearchParams();
      if (!isEmployee && facilityId) q.set("facility_id", facilityId);
      if (showArchived) q.set("include_past", "true");
      if (isAdmin && horizon !== 5) q.set("count", String(horizon));
      const { data } = await api.get(`/availability/upcoming?${q.toString()}`);
      setItems(data || []);
    } catch { setItems([]); }
    if (isEmployee) {
      try {
        const { data } = await api.get("/availability-edit-requests/mine");
        setMyEditReqs(data || []);
      } catch { setMyEditReqs([]); }
    }
    // Load facility min-requirement settings (used to warn/block employees)
    // Employees anchor to their PRIMARY facility (falls back to first facility
    // in the list for legacy accounts without an explicit primary).
    const fid = isEmployee
      ? (user?.primary_facility_id || (user?.facility_ids || [])[0])
      : facilityId;
    if (fid) {
      try {
        const { data } = await api.get(`/facilities/${fid}/settings`);
        setSettings(data);
      } catch { setSettings(null); }
    }
    setLoaded(true);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [facilityId, showArchived, horizon]);

  const pendingEditForPP = (pp_id) =>
    myEditReqs.find(r => r.pay_period_id === pp_id && r.status === "pending");

  const totals = useMemo(() => {
    let d = 0, e = 0, n = 0, w = 0, tot = 0;
    days.forEach((row) => {
      const codes = new Set(row.codes || []);
      if (codes.size === 0) return;
      tot += 1;
      const dt = row.date ? parseISO(row.date) : null;
      if (dt && (dt.getDay() === 0 || dt.getDay() === 6)) w += 1;
      if (codes.has("D")) d += 1;
      if (codes.has("E")) e += 1;
      if (codes.has("N")) n += 1;
    });
    return { total_days: tot, day_shifts: d, evening_shifts: e, night_shifts: n, weekend_days: w };
  }, [days]);

  const violations = useMemo(() => {
    if (!settings) return [];
    const list = [];
    if (totals.total_days     < (settings.min_total_days     || 0)) list.push({ label: "Total days",       need: settings.min_total_days,     have: totals.total_days });
    if (totals.weekend_days   < (settings.min_weekend_days   || 0)) list.push({ label: "Weekend days",     need: settings.min_weekend_days,   have: totals.weekend_days });
    if (totals.day_shifts     < (settings.min_day_shifts     || 0)) list.push({ label: "Day (D) slots",    need: settings.min_day_shifts,     have: totals.day_shifts });
    if (totals.evening_shifts < (settings.min_evening_shifts || 0)) list.push({ label: "Evening (E) slots", need: settings.min_evening_shifts, have: totals.evening_shifts });
    if (totals.night_shifts   < (settings.min_night_shifts   || 0)) list.push({ label: "Night (N) slots",   need: settings.min_night_shifts,   have: totals.night_shifts });
    return list;
  }, [totals, settings]);

  // Codes that mean "already off" — no conflict if the employee unchecks the
  // shift categories for that date. Anything else is a real work assignment.
  const OFF_DAY_CODES = new Set(["PTO", "P", "U", "H", "LOA", "L", "A", "B"]);

  // Hard-block: unavailable on a date where the employee is already scheduled
  // to work. Message steers them to file PTO instead.
  const assignedBlocks = useMemo(() => {
    if (!isEmployee) return [];
    const out = [];
    for (const row of days) {
      if ((row.codes || []).length > 0) continue;   // still available → ok
      const a = openPPAssignments[row.date];
      if (!a) continue;
      let code = a.shift_code;
      if (code === "P") code = a.prev_shift_code || code;
      if (!code || OFF_DAY_CODES.has(code)) continue;
      out.push({ date: row.date, code });
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, openPPAssignments, isEmployee]);

  // Notice cautions: an availability date within `notice_days` of today that
  // the employee is marking as unavailable. Facility settings drive the
  // threshold — Solano is 24 hours (1 day), everyone else defaults to 14 days.
  const noticeThresholdDays = useMemo(() => {
    if (facilityId === "solano") return 1;
    return 14;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityId]);
  const noticeCautions = useMemo(() => {
    if (!isEmployee) return [];
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const out = [];
    for (const row of days) {
      if ((row.codes || []).length > 0) continue;   // still available → ok
      const dt = parseISO(row.date);
      const daysAhead = Math.round((dt - today) / (1000 * 60 * 60 * 24));
      if (daysAhead < 0) continue;                  // don't warn on past
      if (daysAhead >= noticeThresholdDays) continue;
      // Skip if it's already a hard-block (assigned-shift takes precedence)
      if (assignedBlocks.find(b => b.date === row.date)) continue;
      out.push({ date: row.date, daysAhead });
    }
    return out;
  }, [days, isEmployee, noticeThresholdDays, assignedBlocks]);

  // ---------- Employee "not required" state ----------
  if (isEmployee && !user?.availability_required) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center" data-testid="availability-not-required">
        <CalendarClock size={28} className="mx-auto text-slate-300" />
        <div className="mt-3 font-display text-lg font-semibold text-slate-700">Availability isn&apos;t required for your account</div>
        <div className="text-sm text-slate-500 mt-1">If this looks wrong, ask your manager to enable availability submissions on your profile.</div>
      </div>
    );
  }

  // ---------- Editor helpers (employee only) ----------
  const openEditor = (pp) => {
    setOpenPP(pp);
    setReason("");
    const preset = pp.days && pp.days.length ? pp.days : Array.from({ length: (differenceInCalendarDays(parseISO(pp.end), parseISO(pp.start)) + 1) }).map((_, i) => ({
      date: format(addDays(parseISO(pp.start), i), "yyyy-MM-dd"),
      codes: [],
    }));
    setDays(preset);
    // Pull the employee's real assignments for this period so we can hard-block
    // "unmark availability where you're already scheduled". Off-day codes are
    // filtered on the backend side — we still store them all here so the UI
    // can decide (only actual work assignments are flagged).
    setOpenPPAssignments({});
    (async () => {
      try {
        const { data } = await api.get(`/partner-shifts`, {
          params: { facility_id: facilityId, employee_id: user?.id },
        });
        const map = {};
        (data || []).forEach((a) => { map[a.date] = a; });
        setOpenPPAssignments(map);
      } catch { setOpenPPAssignments({}); }
    })();
  };
  const toggle = (idx, slot) => {
    setDays((ds) => ds.map((d, i) => {
      if (i !== idx) return d;
      const has = d.codes.includes(slot);
      return { ...d, codes: has ? d.codes.filter(c => c !== slot) : [...d.codes, slot] };
    }));
  };

  const save = async () => {
    if (!openPP) return;
    if (assignedBlocks.length > 0) {
      toast.error("You're already scheduled on one or more of those dates — file a Time Off request instead.");
      return;
    }
    if (violations.length > 0) {
      toast.error("Availability doesn't meet minimum requirements — see the amber panel above.");
      return;
    }
    setSaving(true);
    try {
      if (openPP.submitted) {
        // Edit an already-submitted availability → request approval instead
        await api.post("/availability-edit-requests", {
          facility_id: facilityId, pay_period_id: openPP.pay_period_id,
          days, reason: reason.trim() || null,
        });
        toast.success("Edit request sent to your manager for approval");
      } else {
        await api.post("/availability", {
          facility_id: facilityId, pay_period_id: openPP.pay_period_id, days,
        });
        toast.success(openPP.is_overdue ? "Submitted (late — flagged for manager)" : "Availability submitted");
      }
      setOpenPP(null);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && Array.isArray(detail.violations)) {
        toast.error(detail.message + " — " + detail.violations.join(" "));
      } else if (detail && typeof detail === "object" && Array.isArray(detail.assigned_dates)) {
        toast.error(`${detail.message} (${detail.assigned_dates.join(", ")})`);
      } else {
        toast.error(formatApiErrorDetail(detail) || "Failed");
      }
    }
    setSaving(false);
  };

  // ---------- Manager view ----------
  if (!isEmployee) {
    const noOne = loaded && items.every(pp => (pp.required_count || 0) === 0);
    const saveAvailDue = async (period_id, due_date) => {
      try {
        await api.put(`/availability-due`, { facility_id: facilityId, period_id, due_date: due_date || null });
        toast.success(due_date ? "Availability due date updated" : "Reverted to default");
        load();
      } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed"); }
    };
    return (
      <div className="rounded-2xl border border-slate-200 bg-white" data-testid="availability-page-manager">
        <div className="p-5 border-b border-slate-200 flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2 text-lg font-display font-semibold">
              <CalendarClock size={18} className="text-blue-600" /> Availability
            </div>
            <div className="text-sm text-slate-500 mt-0.5">
              Track upcoming pay-period availability for the employees you&apos;ve marked as required.
            </div>
          </div>
          <ArchivedToggle showArchived={showArchived} onToggle={setShowArchived} count={items.filter(x => x.is_past).length} />
        </div>
        {isAdmin && (
          <div className="px-5 py-2.5 border-b border-slate-200 bg-slate-50 flex items-center gap-3 flex-wrap text-xs text-slate-700">
            <div className="font-semibold uppercase tracking-widest text-[10px] text-slate-500">Admin</div>
            <div className="flex items-center gap-1">
              <span>Show</span>
              <button
                type="button"
                onClick={() => setHorizon(h => Math.max(1, h - 1))}
                className="w-6 h-6 rounded border border-slate-300 hover:bg-white font-bold"
                data-testid="avail-horizon-down"
              >−</button>
              <span className="tabular-nums w-6 text-center font-semibold">{horizon}</span>
              <button
                type="button"
                onClick={() => setHorizon(h => Math.min(24, h + 1))}
                className="w-6 h-6 rounded border border-slate-300 hover:bg-white font-bold"
                data-testid="avail-horizon-up"
              >+</button>
              <span>upcoming periods</span>
              <span className="text-[10px] text-slate-400">(1–24 · so per-diems can submit further out)</span>
            </div>
            {hiddenIds.size > 0 && (
              <button
                type="button"
                onClick={() => persistHidden(new Set())}
                className="ml-auto text-blue-600 hover:text-blue-800 font-semibold"
                data-testid="avail-restore-hidden"
              >
                Restore {hiddenIds.size} hidden
              </button>
            )}
          </div>
        )}
        <AvailabilityRulesInline facilityId={facilityId} />
        {noOne ? (
          <div className="p-8 text-center text-sm text-slate-500" data-testid="availability-no-required">
            <Users2 size={24} className="mx-auto text-slate-300 mb-2" />
            No employees on this facility are set to submit availability yet.
            <div className="mt-1 text-xs">Turn on <span className="font-medium">Require availability submission</span> on an employee&apos;s profile to see them here.</div>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {items.filter(pp => (showArchived || !pp.is_past) && !hiddenIds.has(pp.pay_period_id)).map((pp) => {
              const daysLeft = differenceInCalendarDays(parseISO(pp.due_date), new Date());
              const pendingCount = (pp.required_count || 0) - (pp.submitted_count || 0);
              const isOpen = expandedIds.has(pp.pay_period_id);
              return (
                <div key={pp.pay_period_id} className={`${pp.is_past ? "bg-slate-50/60" : ""}`} data-testid={`avail-pp-${pp.pay_period_id}`}>
                  <div
                    onClick={(e) => { if (!e.target.closest("button, input")) toggleExpanded(pp.pay_period_id); }}
                    className="w-full text-left p-4 flex items-center justify-between gap-3 flex-wrap hover:bg-slate-50 transition-colors cursor-pointer"
                    data-testid={`avail-toggle-${pp.pay_period_id}`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <ChevronRight size={16} className={`text-slate-400 shrink-0 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                    <div>
                      <div className="font-medium">
                        {mdy(pp.start)} – {mdy(pp.end)}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1.5 flex-wrap">
                        <Clock size={11} />
                        Due {mdy(pp.due_date)}
                        <span className={daysLeft < 0 ? "text-red-700 font-medium" : daysLeft < 7 ? "text-amber-700 font-medium" : ""}>
                          {" · "}
                          {daysLeft < 0 ? `${-daysLeft}d overdue` : `${daysLeft}d left`}
                        </span>
                        {pp.is_past && (
                          <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold rounded-md border border-slate-300 bg-white px-1.5 py-[1px]">
                            Archived
                          </span>
                        )}
                        {pp.due_override && (
                          <span className="text-[10px] uppercase tracking-widest text-fuchsia-700 font-bold">Custom</span>
                        )}
                        {!pp.is_past && (
                          <InlineDueEditor
                            periodId={pp.pay_period_id}
                            value={pp.due_date}
                            hasOverride={pp.due_override}
                            onSave={(v) => saveAvailDue(pp.pay_period_id, v)}
                            onRevert={() => saveAvailDue(pp.pay_period_id, null)}
                          />
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap text-[11px] font-semibold uppercase tracking-widest">
                      <span className="rounded-md border bg-emerald-50 border-emerald-200 text-emerald-800 px-2 py-0.5">
                        {pp.submitted_count}/{pp.required_count} submitted
                      </span>
                      {pp.overdue_count > 0 && (
                        <span className="rounded-md border bg-red-50 border-red-200 text-red-800 px-2 py-0.5 inline-flex items-center gap-1">
                          <AlertTriangle size={11} /> {pp.overdue_count} overdue
                        </span>
                      )}
                      {pp.late_count > 0 && (
                        <span className="rounded-md border bg-amber-50 border-amber-200 text-amber-800 px-2 py-0.5 inline-flex items-center gap-1">
                          <AlertTriangle size={11} /> {pp.late_count} late
                        </span>
                      )}
                      {pendingCount > 0 && pp.overdue_count === 0 && (
                        <span className="rounded-md border bg-fuchsia-50 border-fuchsia-200 text-fuchsia-800 px-2 py-0.5">
                          {pendingCount} pending
                        </span>
                      )}
                      {isAdmin && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); persistHidden(new Set([...hiddenIds, pp.pay_period_id])); }}
                          className="rounded-full w-6 h-6 flex items-center justify-center bg-slate-100 hover:bg-rose-500 hover:text-white text-slate-500 transition-colors"
                          title="Hide this period from the list (admin only)"
                          data-testid={`avail-hide-${pp.pay_period_id}`}
                        >
                          <X size={12} />
                        </button>
                      )}
                    </div>
                  </div>
                  </div>
                  {isOpen && (
                  <div className="mt-3 grid gap-1.5 px-4 pb-4">
                    {pp.employees.map((emp) => {
                      const s = statusPillFor(emp);
                      return (
                        <div key={emp.id} className="flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50/40 p-2" data-testid={`avail-emp-${pp.pay_period_id}-${emp.id}`}>
                          <Avatar className="w-8 h-8 shrink-0">
                            <AvatarImage src={emp.avatar} />
                            <AvatarFallback>{emp.name?.[0]}</AvatarFallback>
                          </Avatar>
                          <div className="min-w-0 flex-1">
                            <div className="font-medium text-sm truncate">{emp.name}</div>
                            <div className="text-[11px] text-slate-500 truncate">
                              {emp.role_label || "Team Member"}
                              {emp.submitted_at && ` · submitted ${mdy(emp.submitted_at)}`}
                            </div>
                          </div>
                          <div className="flex items-center gap-1.5">
                            {(emp.days || []).some(d => (d.codes || []).length > 0) && (
                              <div className="hidden sm:flex items-center gap-0.5">
                                {["D","E","N"].map(c => {
                                  const count = (emp.days || []).reduce((n, d) => n + ((d.codes || []).includes(c) ? 1 : 0), 0);
                                  if (!count) return null;
                                  const M = SLOT_META[c];
                                  return (
                                    <span key={c} className={`text-[10px] font-bold rounded px-1.5 py-0.5 border ${M.cls}`} title={`${M.label}: ${count} days`}>
                                      {c}·{count}
                                    </span>
                                  );
                                })}
                              </div>
                            )}
                            <span className={`inline-flex items-center gap-1 text-[11px] uppercase tracking-widest font-semibold rounded-md border px-2 py-0.5 ${s.cls}`}>
                              {emp.submitted && <Check size={11} />}
                              {(emp.is_overdue || emp.is_late) && <AlertTriangle size={11} />}
                              {s.label}
                            </span>
                            {isManager && (
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); setAdminEdit({ pp, emp }); }}
                                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold rounded-md border border-fuchsia-300 bg-fuchsia-50 hover:bg-fuchsia-100 text-fuchsia-800 px-2 py-1"
                                title="Manager: submit or edit this availability"
                                data-testid={`avail-admin-edit-${pp.pay_period_id}-${emp.id}`}
                              >
                                <ShieldCheck size={11} /> Edit
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {adminEdit && (
          <AdminAvailabilityEditor
            open={!!adminEdit}
            onClose={() => setAdminEdit(null)}
            payPeriodId={adminEdit.pp.pay_period_id}
            facilityId={facilityId}
            employee={adminEdit.emp}
            dateRange={{ start: adminEdit.pp.start, end: adminEdit.pp.end }}
            onSaved={() => { setAdminEdit(null); load(); }}
          />
        )}
      </div>
    );
  }

  // ---------- Employee view (required) ----------
  return (
    <div className="rounded-2xl border border-slate-200 bg-white" data-testid="availability-page">
      <div className="p-5 border-b border-slate-200 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-lg font-display font-semibold">
            <CalendarClock size={18} className="text-blue-600" /> Availability
          </div>
          <div className="text-sm text-slate-500 mt-0.5">
            Tell your manager which shift categories you can work each day. Watch the due dates — your facility&apos;s rule sets when submissions are due.
          </div>
        </div>
        <ArchivedToggle showArchived={showArchived} onToggle={setShowArchived} count={items.filter(x => x.is_past).length} />
      </div>

      <div className="divide-y divide-slate-100">
        {items.filter(pp => showArchived || !pp.is_past).length === 0 && (
          <div className="p-8 text-center text-sm text-slate-500">No upcoming pay periods.</div>
        )}
        {items.filter(pp => showArchived || !pp.is_past).map((pp) => {
          const s = statusPillFor(pp);
          const daysLeft = differenceInCalendarDays(parseISO(pp.due_date), new Date());
          return (
            <div key={pp.pay_period_id} className="p-4" data-testid={`avail-pp-${pp.pay_period_id}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="font-medium">
                    {mdy(pp.start)} – {mdy(pp.end)}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1.5 flex-wrap">
                    <Clock size={11} />
                    Availability due {mdy(pp.due_date)}
                    {!pp.submitted && (
                      <span className={daysLeft < 0 ? "text-red-700 font-medium" : daysLeft < 7 ? "text-amber-700 font-medium" : ""}>
                        {" · "}
                        {daysLeft < 0 ? `${-daysLeft}d overdue` : `${daysLeft}d left`}
                      </span>
                    )}
                    {pp.schedule_post_date && (
                      <span className="text-slate-400">
                        · Schedule posts {mdy(pp.schedule_post_date)}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`inline-flex items-center gap-1 text-[11px] uppercase tracking-widest font-semibold rounded-md border px-2 py-0.5 ${s.cls}`}>
                    {pp.submitted && <Check size={11} />}
                    {(pp.is_overdue || pp.is_late) && <AlertTriangle size={11} />}
                    {s.label}
                  </span>
                  {pendingEditForPP(pp.pay_period_id) ? (
                    <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest font-semibold rounded-md border bg-fuchsia-50 border-fuchsia-300 text-fuchsia-800 px-2 py-0.5" data-testid={`avail-edit-pending-${pp.pay_period_id}`}>
                      <Clock size={11} /> Edit pending approval
                    </span>
                  ) : (
                    <Button size="sm" onClick={() => openEditor(pp)} data-testid={`avail-open-${pp.pay_period_id}`}>
                      {pp.submitted ? "Request edit" : "Submit"}
                    </Button>
                  )}
                </div>
              </div>
              {pp.submitted && pp.days && pp.days.length > 0 && (
                <div className="mt-3 grid grid-cols-7 sm:grid-cols-14 gap-1">
                  {pp.days.map((d) => (
                    <div key={d.date} className="text-center rounded-md bg-slate-50 border border-slate-200 px-1 py-1.5">
                      <div className="text-[9px] uppercase tracking-widest text-slate-500 tabular-nums">
                        {md(d.date)}
                      </div>
                      <div className="mt-0.5 text-[10px] font-bold tabular-nums flex justify-center gap-0.5 flex-wrap">
                        {d.codes && d.codes.length > 0
                          ? d.codes.map((c) => (
                              <span key={c} className={`px-1 rounded ${SLOT_META[c]?.cls || ""}`}>{c}</span>
                            ))
                          : <span className="text-slate-300">—</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {openPP && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm grid place-items-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto thin-scroll shadow-2xl" data-testid="avail-editor">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <div>
                <div className="font-display font-semibold">Availability · {mdy(openPP.start)} – {mdy(openPP.end)}</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  Tick each day for the shift categories you can work. Leave blank if you&apos;re not available.
                </div>
              </div>
              <button className="text-slate-400 hover:text-slate-700" onClick={() => setOpenPP(null)}>✕</button>
            </div>
            <div className="p-4 space-y-2">
              {days.map((d, i) => (
                <div key={d.date} className="flex items-center gap-3 border border-slate-200 rounded-lg p-2" data-testid={`avail-row-${d.date}`}>
                  <div className="w-20 shrink-0">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">{format(parseISO(d.date), "EEE")}</div>
                    <div className="text-sm font-semibold tabular-nums">{md(d.date)}</div>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {["D", "E", "N"].map((slot) => {
                      const active = d.codes.includes(slot);
                      const M = SLOT_META[slot];
                      const Icon = M.icon;
                      return (
                        <button
                          key={slot}
                          onClick={() => toggle(i, slot)}
                          data-testid={`avail-slot-${d.date}-${slot}`}
                          className={`inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-sm font-medium transition-colors ${
                            active ? M.cls : "bg-white text-slate-500 border-slate-300 hover:bg-slate-50"
                          }`}
                        >
                          <Icon size={12} /> {M.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            {assignedBlocks.length > 0 && (
              <div className="mx-4 mb-2 rounded-md border-2 border-red-400 bg-red-50 px-3 py-2 text-[12px] text-red-900 leading-tight" data-testid="avail-assigned-blocks">
                <div className="font-black uppercase tracking-widest text-[10px] flex items-center gap-1">
                  <AlertTriangle size={11} /> Already scheduled — file PTO instead
                </div>
                <div className="mt-1">
                  You&apos;re already assigned a shift on{" "}
                  <b>{assignedBlocks.map(b => mdy(b.date)).join(", ")}</b>.
                  You&apos;ll need to put in a <b>Time Off request</b> for {assignedBlocks.length === 1 ? "that date" : "those dates"} — you can&apos;t revoke availability for a day already on the schedule.
                </div>
              </div>
            )}
            {noticeCautions.length > 0 && (
              <div className="mx-4 mb-2 rounded-md border-2 border-amber-400 bg-amber-50 px-3 py-2 text-[12px] text-amber-900 leading-tight" data-testid="avail-notice-cautions">
                <div className="font-black uppercase tracking-widest text-[10px] flex items-center gap-1">
                  <AlertTriangle size={11} /> {facilityId === "solano" ? "Less than 24 hours notice" : "Less than 14 days notice"}
                </div>
                <div className="mt-1 space-y-0.5">
                  {noticeCautions.map((c) => (
                    <div key={c.date} data-testid={`avail-caution-${c.date}`}>
                      <b>{mdy(c.date)}</b> is {c.daysAhead === 0 ? "today" : `${c.daysAhead} day${c.daysAhead === 1 ? "" : "s"} away`} — less than {facilityId === "solano" ? "24 hours" : "14 days"} notice. This request may result in an occurrence.
                    </div>
                  ))}
                </div>
              </div>
            )}
            {violations.length > 0 && (
              <div className="mx-4 mb-2 rounded-md border-2 border-amber-400 bg-amber-50 px-3 py-2 text-[11px] text-amber-900 leading-tight" data-testid="avail-violations">
                <div className="font-black uppercase tracking-widest text-[10px] flex items-center gap-1">
                  <AlertTriangle size={11} /> Below minimum
                </div>
                <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                  {violations.map((v) => (
                    <div key={v.label}>{v.label}: need <b>{v.need}</b>, have <b>{v.have}</b></div>
                  ))}
                </div>
              </div>
            )}
            <div className="p-4 border-t border-slate-200 flex flex-col sm:flex-row justify-end gap-2 items-stretch sm:items-center sticky bottom-0 bg-white z-10 shadow-[0_-4px_12px_-8px_rgba(0,0,0,0.15)]">
              <div className="mr-auto flex items-center gap-1.5 text-[11px] font-semibold" data-testid="avail-live-counts">
                <span className="px-1.5 py-0.5 rounded bg-slate-200 text-slate-800 tabular-nums">Total {totals.total_days}</span>
                <span className="px-1.5 py-0.5 rounded bg-slate-100 tabular-nums">W {totals.weekend_days}</span>
                <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-900 tabular-nums">D {totals.day_shifts}</span>
                <span className="px-1.5 py-0.5 rounded bg-orange-100 text-orange-900 tabular-nums">E {totals.evening_shifts}</span>
                <span className="px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-900 tabular-nums">N {totals.night_shifts}</span>
              </div>
              {openPP.submitted && (
                <input
                  type="text"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Why the change? (optional note to your manager)"
                  className="flex-1 rounded-md border border-slate-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
                  data-testid="avail-edit-reason"
                />
              )}
              <Button variant="outline" onClick={() => setOpenPP(null)}>Cancel</Button>
              <Button className="bg-blue-600 hover:bg-blue-700"
                onClick={save}
                disabled={saving || violations.length > 0 || assignedBlocks.length > 0}
                data-testid="avail-save">
                {saving ? "Saving…" : openPP.submitted ? "Request edit" : (openPP.is_overdue ? "Submit (late)" : "Submit availability")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
